import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  FileSearch,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { adminApi } from "../../../api/adminApi";
import "./AuditLogs.css";

const PAGE_SIZE = 20;
const EMPTY_PAGINATION = {
  page: 1,
  pageSize: PAGE_SIZE,
  total: 0,
  totalPages: 0,
};
const EMPTY_FILTERS = { search: "", startDate: "", endDate: "" };
const SKELETON_ROWS = 6;

const dateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const presetRange = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { startDate: dateInput(start), endDate: dateInput(end) };
};

const PRESETS = [
  { key: 1, label: "Today" },
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
];

const displayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const displayFilterDate = (value) => {
  if (!value) return "";
  const [y, m, d] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(y, m - 1, d));
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const readableStatus = (value) =>
  value ? String(value).replace(/[_-]/g, " ") : "";

const getDetailsText = (details) => {
  if (!details) return "";
  if (typeof details === "string") {
    try {
      return JSON.stringify(JSON.parse(details), null, 2);
    } catch {
      return details;
    }
  }
  return JSON.stringify(details, null, 2);
};

/* Colour-code the action badge by what it does */
const actionTone = (action = "") => {
  const text = String(action).toLowerCase();
  if (
    /(delete|remov|reject|declin|cancel|fail|suspend|revok|denied)/.test(text)
  )
    return "danger";
  if (/(approv|creat|activat|publish|complet|restor|paid|success)/.test(text))
    return "success";
  if (/(updat|chang|edit|status|assign|modif)/.test(text)) return "info";
  return "neutral";
};

/* 1 … 4 5 [6] 7 8 … 20 */
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("gap");
    out.push(p);
  });
  return out;
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedLog, setExpandedLog] = useState(null);
  const [copiedLog, setCopiedLog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminApi.getAuditLogs({
        ...appliedFilters,
        page,
        pageSize,
      });
      setLogs(Array.isArray(response.data) ? response.data : []);
      setPagination(response.pagination || EMPTY_PAGINATION);
    } catch (loadError) {
      console.error("Could not load audit logs:", loadError);
      setLogs([]);
      setPagination(EMPTY_PAGINATION);
      setError(
        loadError.response?.data?.error ||
          "Could not load audit logs. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize, refreshKey]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const applyNow = (next) => {
    setFilters(next);
    setAppliedFilters({ ...next, search: next.search.trim() });
    setPage(1);
    setExpandedLog(null);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    applyNow(filters);
  };

  const clearFilters = () => applyNow(EMPTY_FILTERS);

  const applyPreset = (days) => applyNow({ ...filters, ...presetRange(days) });

  const removeFilter = (...keys) => {
    const next = { ...appliedFilters };
    keys.forEach((key) => {
      next[key] = "";
    });
    applyNow(next);
  };

  const activePreset = PRESETS.find((preset) => {
    const range = presetRange(preset.key);
    return (
      filters.startDate === range.startDate && filters.endDate === range.endDate
    );
  })?.key;

  const activeChips = [
    appliedFilters.search && {
      id: "search",
      label: `Search: “${appliedFilters.search}”`,
      keys: ["search"],
    },
    (appliedFilters.startDate || appliedFilters.endDate) && {
      id: "dates",
      label:
        appliedFilters.startDate && appliedFilters.endDate
          ? `${displayFilterDate(appliedFilters.startDate)} – ${displayFilterDate(appliedFilters.endDate)}`
          : appliedFilters.startDate
            ? `From ${displayFilterDate(appliedFilters.startDate)}`
            : `Until ${displayFilterDate(appliedFilters.endDate)}`,
      keys: ["startDate", "endDate"],
    },
  ].filter(Boolean);

  const copyDetails = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLog(id);
      setTimeout(
        () => setCopiedLog((current) => (current === id ? null : current)),
        1600,
      );
    } catch {
      setCopiedLog(null);
    }
  };

  const rangeText = useMemo(() => {
    if (!pagination.total) return "No records";
    const first = (pagination.page - 1) * pagination.pageSize + 1;
    const last = Math.min(first + logs.length - 1, pagination.total);
    return `Showing ${first}–${last} of ${pagination.total.toLocaleString()}`;
  }, [logs.length, pagination]);

  const hasFilters = activeChips.length > 0;
  const pages = pageWindow(page, pagination.totalPages);

  return (
    <main className="admin-audit-page">
      <header className="audit-page-header">
        <div className="audit-title-group">
          <span className="audit-title-icon" aria-hidden="true">
            <ShieldCheck size={21} />
          </span>
          <div>
            <span className="audit-eyebrow">GOVERNANCE</span>
            <h1>Audit logs</h1>
            <p>
              Review administrative activity and changes across the platform.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="audit-refresh-button"
          onClick={() => setRefreshKey((key) => key + 1)}
          disabled={loading}
          aria-label="Refresh audit logs"
        >
          <RefreshCw size={15} className={loading ? "audit-spinning" : ""} />
          <span className="audit-btn-label">Refresh</span>
        </button>
      </header>

      <section className="audit-summary" aria-label="Audit log overview">
        <article className="audit-summary-card">
          <span className="audit-summary-icon audit-summary-blue">
            <Activity size={18} aria-hidden="true" />
          </span>
          <div>
            <span>Matching events</span>
            <strong>{pagination.total.toLocaleString()}</strong>
          </div>
        </article>
        <article className="audit-summary-card">
          <span className="audit-summary-icon audit-summary-violet">
            <FileSearch size={18} aria-hidden="true" />
          </span>
          <div>
            <span>Current page</span>
            <strong>
              {pagination.totalPages
                ? `${page} / ${pagination.totalPages}`
                : "—"}
            </strong>
          </div>
        </article>
        <article className="audit-summary-card audit-summary-context">
          <span className="audit-summary-icon audit-summary-green">
            <Clock3 size={18} aria-hidden="true" />
          </span>
          <div>
            <span>Record range</span>
            <strong>{rangeText}</strong>
          </div>
        </article>
      </section>

      <form className="audit-filter-panel" onSubmit={applyFilters}>
        <div className="audit-filter-heading">
          <div>
            <h2>Activity history</h2>
            <p>Search by action, user, email, or request number.</p>
          </div>
          <div
            className="audit-presets"
            role="group"
            aria-label="Quick date range"
          >
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={activePreset === preset.key ? "is-active" : ""}
                aria-pressed={activePreset === preset.key}
                onClick={() => applyPreset(preset.key)}
                disabled={loading}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="audit-filter-controls">
          <label className="audit-search-field">
            <span>Search activity</span>
            <span className="audit-input-wrap">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="e.g. request approved, name, request #"
                maxLength={120}
              />
            </span>
          </label>
          <label>
            <span>From date</span>
            <span className="audit-input-wrap">
              <CalendarDays size={15} aria-hidden="true" />
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                max={filters.endDate || undefined}
                onChange={handleFilterChange}
              />
            </span>
          </label>
          <label>
            <span>To date</span>
            <span className="audit-input-wrap">
              <CalendarDays size={15} aria-hidden="true" />
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={handleFilterChange}
              />
            </span>
          </label>
          <div className="audit-filter-actions">
            <button
              type="submit"
              className="audit-apply-button"
              disabled={loading}
            >
              <Search size={15} />
              Apply filters
            </button>
            <button
              type="button"
              className="audit-clear-button"
              onClick={clearFilters}
              disabled={loading}
              aria-label="Clear filters"
              title="Clear filters"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {hasFilters && (
          <div className="audit-chips" aria-label="Active filters">
            <span className="audit-chips-label">Active filters</span>
            {activeChips.map((chip) => (
              <span className="audit-chip" key={chip.id}>
                {chip.label}
                <button
                  type="button"
                  onClick={() => removeFilter(...chip.keys)}
                  aria-label={`Remove filter ${chip.label}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="audit-chips-clear"
              onClick={clearFilters}
            >
              Clear all
            </button>
          </div>
        )}
      </form>

      {error && (
        <div className="audit-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshKey((key) => key + 1)}>
            Try again
          </button>
        </div>
      )}

      <section
        className="audit-table-panel"
        aria-label="Audit events"
        aria-busy={loading}
      >
        <div className="audit-table-meta">
          <span>{rangeText}</span>
          <label>
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              aria-label="Rows per page"
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        {!loading && logs.length === 0 ? (
          <div className="audit-state">
            <span className="audit-empty-icon">
              <FileSearch size={23} />
            </span>
            <strong>
              {error ? "Audit records unavailable" : "No audit events found"}
            </strong>
            <span>
              {error
                ? "Check the server connection and try again."
                : "Try changing your search or date filters."}
            </span>
            {!error && hasFilters && (
              <button type="button" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="audit-table-scroll">
            <table className="audit-table">
              <thead>
                <tr>
                  <th scope="col">Actor</th>
                  <th scope="col">Action</th>
                  <th scope="col">Request / status change</th>
                  <th scope="col">Date &amp; time</th>
                  <th scope="col">
                    <span className="audit-sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({
                      length: Math.min(pageSize, SKELETON_ROWS),
                    }).map((_, index) => (
                      <tr
                        className="audit-skeleton-row"
                        key={`sk-${index}`}
                        aria-hidden="true"
                      >
                        <td>
                          <div className="audit-actor">
                            <span className="audit-skeleton audit-skeleton-avatar" />
                            <span className="audit-skeleton-stack">
                              <span
                                className="audit-skeleton"
                                style={{ width: 120 }}
                              />
                              <span
                                className="audit-skeleton"
                                style={{ width: 160 }}
                              />
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className="audit-skeleton"
                            style={{ width: 110 }}
                          />
                        </td>
                        <td>
                          <span
                            className="audit-skeleton"
                            style={{ width: 140 }}
                          />
                        </td>
                        <td>
                          <span
                            className="audit-skeleton"
                            style={{ width: 120 }}
                          />
                        </td>
                        <td />
                      </tr>
                    ))
                  : logs.map((log) => {
                      const isExpanded = expandedLog === log.AuditLogID;
                      const details = getDetailsText(log.Details);
                      return (
                        <React.Fragment key={log.AuditLogID}>
                          <tr className={isExpanded ? "is-expanded" : ""}>
                            <td>
                              <div className="audit-actor">
                                <span className="audit-avatar">
                                  {initials(log.FullName)}
                                </span>
                                <span className="audit-actor-copy">
                                  <strong>
                                    {log.FullName || "System / removed user"}
                                  </strong>
                                  <small>
                                    {log.Email ||
                                      (log.UserID
                                        ? `ID ${log.UserID}`
                                        : "System")}
                                  </small>
                                </span>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`audit-action audit-action-${actionTone(log.Action)}`}
                              >
                                {log.Action}
                              </span>
                            </td>
                            <td>
                              <div className="audit-change">
                                {log.RequestNumber && (
                                  <span className="audit-request-number">
                                    {log.RequestNumber}
                                  </span>
                                )}
                                {log.PreviousStatus || log.NewStatus ? (
                                  <span className="audit-status-flow">
                                    {log.PreviousStatus && (
                                      <span className="audit-status">
                                        {readableStatus(log.PreviousStatus)}
                                      </span>
                                    )}
                                    {log.PreviousStatus && log.NewStatus && (
                                      <ChevronRight
                                        size={13}
                                        aria-label="changed to"
                                      />
                                    )}
                                    {log.NewStatus && (
                                      <span className="audit-status audit-status-new">
                                        {readableStatus(log.NewStatus)}
                                      </span>
                                    )}
                                  </span>
                                ) : !log.RequestNumber ? (
                                  <span className="audit-no-change">—</span>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <time
                                className="audit-time"
                                dateTime={log.CreatedAt || undefined}
                              >
                                {displayDate(log.CreatedAt)}
                              </time>
                            </td>
                            <td>
                              {details && (
                                <button
                                  type="button"
                                  className="audit-expand-button"
                                  aria-label={`${isExpanded ? "Hide" : "Show"} details for ${log.Action}`}
                                  aria-expanded={isExpanded}
                                  onClick={() =>
                                    setExpandedLog(
                                      isExpanded ? null : log.AuditLogID,
                                    )
                                  }
                                >
                                  <ChevronDown size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                          {isExpanded && details && (
                            <tr className="audit-details-row">
                              <td colSpan="5">
                                <div className="audit-details-content">
                                  <div className="audit-details-head">
                                    <strong>Event details</strong>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyDetails(log.AuditLogID, details)
                                      }
                                    >
                                      {copiedLog === log.AuditLogID ? (
                                        <>
                                          <Check size={13} /> Copied
                                        </>
                                      ) : (
                                        <>
                                          <Copy size={13} /> Copy
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  <pre>{details}</pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
          </div>
        )}

        <footer className="audit-pagination">
          <span className="audit-pagination-range">{rangeText}</span>
          <nav aria-label="Pagination">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={loading || page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
              <span className="audit-btn-label">Previous</span>
            </button>

            <span className="audit-page-numbers">
              {pages.map((p, index) =>
                p === "gap" ? (
                  <span
                    className="audit-page-gap"
                    key={`gap-${index}`}
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : (
                  <button
                    type="button"
                    key={p}
                    className={p === page ? "is-current" : ""}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? "page" : undefined}
                    onClick={() => setPage(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                ),
              )}
            </span>
            <span className="audit-page-compact">
              {pagination.totalPages ? page : 0} / {pagination.totalPages}
            </span>

            <button
              type="button"
              onClick={() =>
                setPage((current) =>
                  Math.min(pagination.totalPages, current + 1),
                )
              }
              disabled={loading || page >= pagination.totalPages}
              aria-label="Next page"
            >
              <span className="audit-btn-label">Next</span>
              <ChevronRight size={16} />
            </button>
          </nav>
        </footer>
      </section>
    </main>
  );
};

export default AuditLogs;
